import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../store/auth';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/Login.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      name: 'StudentDashboard',
      component: () => import('../views/StudentDashboard.vue'),
      meta: { requiresAuth: true, roles: ['student'] },
    },
    {
      path: '/vakplanner',
      name: 'Vakplanner',
      component: () => import('../views/Vakplanner.vue'),
      meta: { requiresAuth: true, roles: ['student'] },
    },
    {
      path: '/weekplanner',
      name: 'Weekplanner',
      component: () => import('../views/Weekplanner.vue'),
      meta: { requiresAuth: true, roles: ['student'] },
    },
    {
      path: '/weekplanner/week/:weekNr',
      name: 'WeekplannerWeek',
      component: () => import('../views/WeekplannerWeek.vue'),
      meta: { requiresAuth: true, roles: ['student'] },
    },
    {
      path: '/teacher/dashboard',
      name: 'TeacherDashboard',
      component: () => import('../views/TeacherDashboard.vue'),
      meta: { requiresAuth: true, roles: ['teacher'] },
    },
    {
      path: '/teacher/voortgang',
      name: 'TeacherProgress',
      component: () => import('../views/TeacherProgress.vue'),
      meta: { requiresAuth: true, roles: ['teacher'] },
    },
    {
      path: '/teacher',
      name: 'TeacherVakplanner',
      component: () => import('../views/TeacherVakplanner.vue'),
      meta: { requiresAuth: true, roles: ['teacher'] },
    },
    {
      path: '/admin',
      name: 'AdminDashboard',
      component: () => import('../views/AdminDashboard.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/users',
      name: 'AdminUsers',
      component: () => import('../views/admin/Users.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/jobs',
      name: 'AdminJobs',
      component: () => import('../views/admin/Jobs.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/periodeplanners',
      name: 'AdminPeriodeplanners',
      component: () => import('../views/admin/Periodeplanners.vue'),
      meta: { requiresToken: true, requiresAdmin: true },
    },
    {
      path: '/admin/uploads',
      name: 'AdminUploads',
      component: () => import('../views/admin/Uploads.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/persons',
      name: 'AdminPersons',
      component: () => import('../views/admin/Persons.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/jaarplanning',
      name: 'AdminJaarplanning',
      component: () => import('../views/admin/Jaarplanning.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/toetskalender',
      name: 'AdminToetskalender',
      component: () => import('../views/admin/Toetskalender.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/toetskalender/overzicht',
      name: 'AdminToetskalenderOverzicht',
      component: () => import('../views/admin/ToetskalenderOverview.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/validation-rules',
      name: 'AdminValidationRules',
      component: () => import('../views/admin/ValidationRules.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/validation-results',
      name: 'AdminValidationResults',
      component: () => import('../views/admin/ValidationResults.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/controle',
      name: 'AdminControle',
      component: () => import('../views/admin/Controle.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/ip-blocking',
      name: 'AdminIPBlocking',
      component: () => import('../views/admin/IPBlocking.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/security',
      name: 'AdminSecurityDashboard',
      component: () => import('../views/admin/SecurityDashboard.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
    {
      path: '/admin/monitoring',
      name: 'AdminMonitoring',
      component: () => import('../views/admin/Monitoring.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] },
    },
  ],
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Ensure auth store is initialized (in case it wasn't called in main.js)
  if (!authStore.token && !authStore.user) {
    authStore.init();
  }

  // Prevent redirect loops - if we're already on the target path, just continue
  if (to.path === from.path) {
    next();
    return;
  }

  // Check authentication for protected routes
  if (to.meta.requiresAuth) {
    // If we have a token but no user, try to fetch user info
    if (authStore.token && !authStore.user) {
      try {
        await authStore.fetchMe();
        if (!authStore.user) {
          // Token is invalid, clear auth and redirect to login
          authStore.clearAuth();
          if (to.path !== '/login') {
            next({ path: '/login', query: { redirect: to.fullPath } });
            return;
          }
        }
      } catch (error) {
        // Only redirect on authentication errors (401), not on timeouts or network errors
        const isAuthError = error.response?.status === 401;
        const isGoingToLogin = to.path === '/login';
        
        if (isAuthError && !isGoingToLogin) {
          // Token is invalid (401), clear auth and redirect to login
          // But only if we're not already going to login
          authStore.clearAuth();
          next({ path: '/login', query: { redirect: to.fullPath } });
          return;
        } else if (isAuthError && isGoingToLogin) {
          // Already going to login, just clear auth and continue
          authStore.clearAuth();
          next();
          return;
        } else {
          // Network error, timeout, or other error - don't redirect, just continue
          // The component can handle the error itself
          console.warn('Failed to fetch user info (non-auth error):', error.message);
          // Allow navigation but user won't be set - component should handle this
        }
      }
    }

    // Check if authenticated (token exists and user is set)
    if (!authStore.isAuthenticated || !authStore.user) {
      // Not authenticated - redirect to login, but only if not already going there
      const isGoingToLogin = to.path === '/login';
      if (!isGoingToLogin) {
        next({ path: '/login', query: { redirect: to.fullPath } });
        return;
      }
      // If already going to login, just allow it - no redirect needed
      next();
      return;
    }

    // Check role authorization (only if authenticated)
    if (to.meta.roles && authStore.user && !to.meta.roles.includes(authStore.user.role)) {
      // User doesn't have required role, redirect to home
      if (to.path !== '/') {
        next('/');
        return;
      }
    }
  }

  // Redirect authenticated users away from login page
  if (to.path === '/login' && authStore.isAuthenticated && authStore.user) {
    // Already logged in, redirect to role-specific default page
    if (authStore.user.role === 'administrator') {
      next('/admin');
    } else if (authStore.user.role === 'teacher') {
      next('/teacher/dashboard');
    } else {
      next('/');
    }
    return;
  }

  // Allow navigation
  next();
});

export default router;

